import React from "react";
import {ConfirmModal, TableData, useNotifications} from "peggirkit";
import {FormattedMessage, useIntl} from "react-intl";
import {useMutation} from "@tanstack/react-query";
import {notifyDanger, notifySuccess} from "@/utils/NotifyUtils";
import {DeliveryArea} from "@/data/entities/deliveryArea";
import {useRestaurant} from "@/context/restaurant/RestaurantProvider";
import {SetStateFn} from "@/utils/SetStateUtils";
import {api} from "@/data/api";

export type DeleteDeliveryAreaState = {
    showModal: boolean,
    deliveryArea: DeliveryArea | null,
};

type Props = {
    show: boolean,
    setShow: (show: boolean) => void,
    deliveryArea: DeliveryArea | null,
    setTableData: SetStateFn<TableData<DeliveryArea>[]>,
};

const DeleteDeliveryAreaModal = ({show, setShow, deliveryArea, setTableData}: Props) => {
    const intl = useIntl();
    const {notify} = useNotifications();
    const {dashboardLocation} = useRestaurant();
    const {mutate, isPending} = useMutation({
        mutationFn: (deliveryAreaId: number) => api.deliveryArea.deleteDeliveryArea(dashboardLocation.id, deliveryAreaId),
        onSuccess: (result) => {
            setTableData(result);
            setShow(false);
            notifySuccess(intl.formatMessage({id: "deleted"}), notify)
        },
        onError: () => {
            notifyDanger(intl.formatMessage({id: "systemError"}), notify);
        },
    });

    if (deliveryArea === null) {
        return <></>;
    }

    return (
        <ConfirmModal
            type={"danger"}
            show={show}
            onClose={() => setShow(false)}
            title={intl.formatMessage({id: "deleteDeliveryArea"})}
            dismissButton={{
                text: intl.formatMessage({id: "cancel"}),
            }}
            continueButton={{
                text: intl.formatMessage({id: "continue"}),
                onClick: () => mutate(deliveryArea.id),
                props: {color: "danger"},
            }}
            disabled={isPending}
            loading={isPending}
            position={"center-bottom"}
            breakpoints={{md: {position: "center"}}}
        >
            <FormattedMessage
                id={"confirmDeleteDeliveryArea"}
                values={{
                    postalCode: deliveryArea.postalCode,
                    locationName: dashboardLocation.name,
                }}
            />
        </ConfirmModal>
    );
};

export default DeleteDeliveryAreaModal;